import { FC, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowRight, ArrowUpRight, X } from "lucide-react"
import type { InsightDismissReason } from "@bb/agent-contract"
import {
  getStaffBriefing,
  Briefing,
  BriefingItem
} from "../../../api/staff-agent"
import {
  markAssistantNavigation,
  trackAssistantEvent
} from "../../utils/assistant-analytics"

/**
 * "What's new for you" block in the assistant's empty state. Each item is a
 * single clickable row: the insight itself seeds a chat investigation (the
 * item's investigateChip is sent verbatim), and the optional trailing icon
 * opens the relevant admin page. Renders nothing while loading or on
 * failure — the static greeting copy underneath always stands on its own.
 *
 * Dismissing asks for one of the contract's insight reasons (a separate set
 * from response-feedback reasons) and hides the insight on this device for a
 * day. It is telemetry, not feedback: nothing is stored server-side.
 */
interface BriefingGreetingProps {
  onInvestigate: (message: string) => void
}

// Record<> keeps this exhaustive against the contract at compile time.
const DISMISS_REASONS: Record<InsightDismissReason, string> = {
  irrelevant: "Not relevant",
  already_handled: "Already handled",
  incorrect: "Looks wrong",
  too_frequent: "Too frequent"
}

const DISMISSED_KEY = "staffAiDismissedInsights"
const DISMISS_TTL_MS = 24 * 60 * 60_000

/** Local identity for hiding; only `key` ever leaves the browser. */
const insightId = (item: BriefingItem) => item.key ?? item.insight

export function readDismissed(now: number = Date.now()): Set<string> {
  try {
    const stored = JSON.parse(localStorage.getItem(DISMISSED_KEY) || "{}")
    return new Set(
      Object.entries(stored)
        .filter(([, until]) => typeof until === "number" && until > now)
        .map(([id]) => id)
    )
  } catch {
    return new Set()
  }
}

export function rememberDismissed(id: string, now: number = Date.now()): void {
  const live: Record<string, number> = {}
  readDismissed(now).forEach(existing => {
    live[existing] = now + DISMISS_TTL_MS
  })
  live[id] = now + DISMISS_TTL_MS
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(live))
  } catch {
    /* private mode: the dismissal just won't persist */
  }
}

const BriefingGreeting: FC<BriefingGreetingProps> = ({ onInvestigate }) => {
  const navigate = useNavigate()
  const [briefing, setBriefing] = useState<Briefing | null>(null)
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed())
  const [dismissing, setDismissing] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    getStaffBriefing()
      .then(b => {
        if (mounted) setBriefing(b)
      })
      .catch(() => {
        /* greeting falls back to the static copy */
      })
    return () => {
      mounted = false
    }
  }, [])

  const items = (briefing?.items ?? []).filter(
    item => !dismissed.has(insightId(item))
  )
  if (!items.length) return null

  const dismiss = (item: BriefingItem, reason: InsightDismissReason) => {
    trackAssistantEvent("insight_dismissed", {
      insight: item.key ?? "unknown",
      reason
    })
    rememberDismissed(insightId(item))
    setDismissed(readDismissed())
    setDismissing(null)
  }

  return (
    <div className="w-full max-w-xl rounded-xl border border-violet-100 bg-violet-50/60 p-3 text-left">
      <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
        What&apos;s new for you
      </p>
      <ul className="space-y-0.5">
        {items.map(item => (
          <li key={item.insight}>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  trackAssistantEvent("insight_opened", {
                    insight: item.key ?? "unknown",
                    via: "ask"
                  })
                  onInvestigate(item.investigateChip)
                }}
                title={item.investigateChip}
                className="group flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white"
              >
                <span className="text-sm text-neutral-700">{item.insight}</span>
                <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-violet-500 opacity-60 transition-opacity group-hover:opacity-100">
                  Ask
                  <ArrowRight size={12} />
                </span>
              </button>
              {item.platformLink && (
                <button
                  type="button"
                  onClick={() => {
                    markAssistantNavigation()
                    trackAssistantEvent("insight_opened", {
                      insight: item.key ?? "unknown",
                      via: "link"
                    })
                    navigate(item.platformLink!)
                  }}
                  title="Open in admin"
                  className="shrink-0 rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-white hover:text-violet-700"
                >
                  <ArrowUpRight size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  setDismissing(current =>
                    current === insightId(item) ? null : insightId(item)
                  )
                }
                aria-label="Dismiss this insight"
                aria-expanded={dismissing === insightId(item)}
                title="Dismiss"
                className="shrink-0 rounded-lg p-1.5 text-neutral-300 transition-colors hover:bg-white hover:text-neutral-600"
              >
                <X size={14} />
              </button>
            </div>
            {dismissing === insightId(item) && (
              <div className="flex flex-wrap items-center gap-1.5 px-2 pb-1.5 pt-0.5">
                <span className="text-[11px] text-neutral-500">Why?</span>
                {(
                  Object.entries(DISMISS_REASONS) as [
                    InsightDismissReason,
                    string
                  ][]
                ).map(([reason, label]) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => dismiss(item, reason)}
                    className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-neutral-600 transition-colors hover:border-violet-300 hover:text-violet-700"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default BriefingGreeting
